import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrg } from '../context/OrgContext';
import { chalanAPI, vehicleTypeAPI } from '../services/chalanService';

function CreateChalan() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const orgContext = useOrg();
  const orgPrefix = `/${(orgContext?.orgCode || localStorage.getItem('orgCode') || 'rta').toLowerCase()}`;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [autoCalcFee, setAutoCalcFee] = useState(true);
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);

  const [formData, setFormData] = useState({
    owner_name: '',
    owner_cnic: '',
    owner_phone: '',
    car_number: '',
    permit: '',
    vehicle_type: '',
    violation_description: '',
    fees_amount: '',
    issue_location: '',
    remarks: '',
  });

  // Fetch vehicle types
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const response = await vehicleTypeAPI.getVehicleTypes();
        const types = response.data.results || response.data || [];
        setVehicleTypes(types);
      } catch (err) {
        console.error('Error fetching vehicle types:', err);
      }
    };
    fetchVehicleTypes();
  }, []);

  // Auto-calculate fee when vehicle type changes
  useEffect(() => {
    if (autoCalcFee && formData.vehicle_type && formData.vehicle_type !== '') {
      const vehicleType = vehicleTypes.find((vt) => vt.id === parseInt(formData.vehicle_type));
      if (vehicleType) {
        setSelectedVehicleType(vehicleType);
        // The fee will be auto-calculated on the backend
        setFormData((prev) => ({
          ...prev,
          fees_amount: '', // Let backend calculate
        }));
      }
    }
  }, [formData.vehicle_type, autoCalcFee, vehicleTypes]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.owner_name.trim()) {
      setError(t('createChalan.validationOwnerName'));
      return false;
    }
    if (!formData.owner_cnic.trim()) {
      setError(t('createChalan.validationOwnerCnic'));
      return false;
    }
    if (!formData.car_number.trim()) {
      setError(t('createChalan.validationCarNumber'));
      return false;
    }
    if (!formData.vehicle_type) {
      setError(t('createChalan.validationVehicleType'));
      return false;
    }
    if (!formData.violation_description.trim()) {
      setError(t('createChalan.validationViolationDesc'));
      return false;
    }
    if (!autoCalcFee && !formData.fees_amount) {
      setError(t('createChalan.validationFeesAmount'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        ...formData,
        auto_calculate_fee: autoCalcFee,
        vehicle_type: parseInt(formData.vehicle_type),
      };

      // Remove empty optional fields
      if (!submitData.fees_amount) {
        delete submitData.fees_amount;
      }
      if (!submitData.permit) {
        delete submitData.permit;
      }

      const response = await chalanAPI.createChalan(submitData);

      setSuccess(true);
      setTimeout(() => {
        navigate(`${orgPrefix}/chalans/${response.data.id}`);
      }, 1500);
    } catch (err) {
      console.error('Error creating chalan:', err);
      setError(
        err.response?.data?.detail ||
        err.response?.data?.non_field_errors?.[0] ||
        t('createChalan.errorFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      owner_name: '',
      owner_cnic: '',
      owner_phone: '',
      car_number: '',
      permit: '',
      vehicle_type: '',
      violation_description: '',
      fees_amount: '',
      issue_location: '',
      remarks: '',
    });
    setAutoCalcFee(true);
    setSelectedVehicleType(null);
    setError(null);
  };

  return (
    <Box sx={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      <Typography variant="h4" sx={{ marginBottom: '20px', fontWeight: 'bold' }}>
        {t('createChalan.pageTitle')}
      </Typography>

      {error && <Alert severity="error" sx={{ marginBottom: '20px' }}>{error}</Alert>}
      {success && (
        <Alert severity="success" sx={{ marginBottom: '20px' }}>
          {t('createChalan.successMsg')}
        </Alert>
      )}

      <Paper sx={{ padding: '30px' }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid size={12}>
              <Typography variant="h6" sx={{ marginBottom: '15px', fontWeight: 'bold', color: '#1976d2' }}>
                {t('createChalan.ownerInfo')}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('createChalan.ownerNameLabel')}
                name="owner_name"
                value={formData.owner_name}
                onChange={handleInputChange}
                placeholder={t('createChalan.ownerNamePlaceholder')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('createChalan.ownerCnicLabel')}
                name="owner_cnic"
                value={formData.owner_cnic}
                onChange={handleInputChange}
                placeholder={t('createChalan.ownerCnicPlaceholder')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('createChalan.phoneLabel')}
                name="owner_phone"
                value={formData.owner_phone}
                onChange={handleInputChange}
                placeholder={t('createChalan.phonePlaceholder')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('createChalan.carNumberLabel')}
                name="car_number"
                value={formData.car_number}
                onChange={handleInputChange}
                placeholder={t('createChalan.carNumberPlaceholder')}
              />
            </Grid>

            {/* Vehicle & Permit Information */}
            <Grid size={12}>
              <Typography variant="h6" sx={{ marginBottom: '15px', fontWeight: 'bold', color: '#1976d2' }}>
                {t('createChalan.vehicleViolationInfo')}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label={t('createChalan.vehicleTypeLabel')}
                name="vehicle_type"
                value={formData.vehicle_type}
                onChange={handleInputChange}
                SelectProps={{ native: true }}
              >
                <option value="">{t('createChalan.selectVehicleType')}</option>
                {vehicleTypes.map((vt) => (
                  <option key={vt.id} value={vt.id}>
                    {vt.name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('createChalan.permitIdLabel')}
                name="permit"
                value={formData.permit}
                onChange={handleInputChange}
                placeholder={t('createChalan.permitIdPlaceholder')}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('createChalan.violationDescLabel')}
                name="violation_description"
                value={formData.violation_description}
                onChange={handleInputChange}
                placeholder={t('createChalan.violationDescPlaceholder')}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('createChalan.issueLocationLabel')}
                name="issue_location"
                value={formData.issue_location}
                onChange={handleInputChange}
                placeholder={t('createChalan.issueLocationPlaceholder')}
              />
            </Grid>

            {/* Fee Information */}
            <Grid size={12}>
              <Typography variant="h6" sx={{ marginBottom: '15px', fontWeight: 'bold', color: '#1976d2' }}>
                {t('createChalan.feeInfo')}
              </Typography>
            </Grid>

            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoCalcFee}
                    onChange={(e) => setAutoCalcFee(e.target.checked)}
                  />
                }
                label={t('createChalan.autoCalcLabel')}
              />
            </Grid>

            {selectedVehicleType && autoCalcFee && (
              <Grid size={12}>
                <Card sx={{ backgroundColor: '#e8f5e9' }}>
                  <CardContent>
                    <Typography>
                      {t('createChalan.selectedVehicleType', { name: selectedVehicleType.name })}
                    </Typography>
                    <Typography variant="small" color="textSecondary">
                      {t('createChalan.autoCalcNote')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {!autoCalcFee && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={t('createChalan.feesAmountLabel')}
                  name="fees_amount"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={formData.fees_amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                />
              </Grid>
            )}

            {/* Additional Information */}
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('createChalan.remarksLabel')}
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                placeholder={t('createChalan.remarksPlaceholder')}
              />
            </Grid>

            {/* Buttons */}
            <Grid size={12} sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading}
              >
                {t('common.reset')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? t('createChalan.creating') : t('createChalan.createChalan')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default CreateChalan;
